import React, { useEffect, useState } from "react";
import "./App.css";
import { Navigate, Route, Routes } from "react-router-dom";
import { createTheme, CssBaseline, ThemeProvider } from "@mui/material";
import DarkMode from "../src/assets/images/mode-dark.svg";
import LightMode from "../src/assets/images/mode-light.svg";
import PrivateRoute from "./router/PrivateRoute";
import AppRoutes from "./router/routes";
import { RouteType } from "./router/type";
import { useDispatch, useSelector } from "react-redux";
import { setStripeMetadata, setUser } from "./rtkSlices/metaDataSlice";
import { useGetUserProfileQuery, usePaymentLinksQuery } from "./services/user";
import { RootState } from "./store";
import { UserInfo } from "./types/userInfo";

interface PaymentLink {
  name: string;
  stripe_url: string;
  price: number;
}

function App() {
  const [mode, setMode] = useState(localStorage.getItem("theme"));
  const dispatch = useDispatch();
  const metaData = useSelector((state: RootState) => state.metaData);
  const userId = metaData.userInfo["cognito:username"];
  const getUserProfile = useGetUserProfileQuery(`?username=${userId}`, {
    skip: !userId,
  });
  const getPaymentLinks = usePaymentLinksQuery(null, {});
  let userInfo: UserInfo | null = null;
  const storedUserInfo = localStorage.getItem("userInfo");
  if (storedUserInfo) {
    try {
      userInfo = JSON.parse(storedUserInfo);
    } catch (err) {
      console.error("Invalid userInfo JSON:", err);
    }
  }
  useEffect(() => {
    if (userInfo) {
      userInfo.nav = false;
      if (userInfo) {
        dispatch(setUser(userInfo));
      }
    }
  }, []);

  useEffect(() => {
    if (!getPaymentLinks.isFetching && getPaymentLinks.isSuccess) {
      if (getPaymentLinks.data && getPaymentLinks.data.length) {
        const paymentLinks = getPaymentLinks.data.map(
          (payment: PaymentLink) => ({
            name: payment.name,
            url: payment.stripe_url,
            price: payment.price,
          })
        );
        dispatch(setStripeMetadata({ paymentLinks: paymentLinks }));
      }
    }
  }, [getPaymentLinks.isFetching]);

  React.useEffect(() => {
    if (!getUserProfile.isFetching && getUserProfile.isSuccess) {
      if (getUserProfile.data) {
        try {
          // if (userInfo) {
          const {
            firstName,
            lastName,
            role,
            phone,
            preferences,
            profile_picture,
            subscriptionStatus,
            isOnboardingCompleted,
            personal_thread_id,
            personal_thread_llm,
            email,
          } = getUserProfile.data.user;
          userInfo = {
            ...userInfo,
            firstName,
            lastName,
            role,
            phone,
            preferences,
            profile_picture,
            subscriptionStatus,
            isOnboardingCompleted,
            personal_thread_id,
            personal_thread_llm,
            email,
          };
          dispatch(setUser(userInfo));
          // }
        } catch (err) {
          console.error("Error parsing userInfo:", err);
          localStorage.removeItem("userInfo"); // optional recovery
          userInfo = null;
        }
      }
    }
  }, [getUserProfile.isFetching]);

  const darkTheme = createTheme({
    palette: {
      mode: "dark",
      background: {
        default: "#0b2b1b",
      },
      primary: {
        main: "#ffffff",
        contrastText: "#F2FCF7",
      },
      secondary: {
        main: "rgba(33, 143, 84, 1)",
      },
      success: {
        main: "#2C4A3A",
      },
      action: {
        disabledBackground: "rgba(52, 180, 116, 0.25)",
        disabled: "grey",
      },
    },
    components: {
      MuiButtonBase: {
        styleOverrides: {
          root: {
            color: "#ffffff",
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            color: "#ffffff !important",
          },
        },
      },
    },
    cssVariables: true,
    typography: {
      fontFamily: `"Poppins", sans-serif`,
      fontWeightBold: 600,
    },
  });

  const lightTheme = createTheme({
    palette: {
      mode: "light",
      background: {
        default: "#ffffff",
      },
      primary: {
        main: "#111111",
      },
      secondary: {
        main: "rgba(33, 143, 84, 1)",
        contrastText: "#2C4A3A",
      },
      success: {
        main: "#ffffff",
      },
    },
    components: {
      MuiButtonBase: {
        styleOverrides: {
          root: {
            color: "#333333",
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            color: "#333333 !important",
          },
        },
      },
    },
    cssVariables: true,
    typography: {
      fontFamily: `"Poppins", sans-serif`,
      fontWeightBold: 600,
    },
  });

  return (
    <ThemeProvider theme={mode == "dark" ? darkTheme : lightTheme}>
      <CssBaseline />
      <img
        className="mode-toggle"
        src={mode == "dark" ? DarkMode : LightMode}
        onClick={() => {
          localStorage.setItem("theme", mode == "dark" ? "light" : "dark");
          setMode(mode == "dark" ? "light" : "dark");
        }}
        id="theme-toggle"
      />
      <AppRoutes />
    </ThemeProvider>
  );
}

export default App;
